import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import useMessageStream from '../hooks/useMessageStream';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Calendar, Clock, Users, DollarSign, ArrowRight, Gift, FileText, Download, CreditCard, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { RescheduleModal } from '../components/RescheduleModal';
import { BookingStatusBadge } from '../components/BookingStatusBadge';
import { CustomerEditBookingDialog } from '../components/CustomerEditBookingDialog';
import { GuestAllergyForms } from '../components/GuestAllergyForms';
import { BookingDiscountsWidget, BookingTotalsSummary } from '../components/BookingDiscountsWidget';
import { PaymentModal } from '../components/PaymentModal';
import { AddonRedeemPanel } from '../components/AddonRedeemPanel';
import { Pencil, Mail, PartyPopper } from 'lucide-react';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mirrors backend _booking_outstanding so the customer sees the same balance.
const remainingOf = (b) => {
  const total = Number(b?.total || 0);
  const dep = Number(b?.deposit_amount || 0);
  const manual = Number(b?.manual_paid_amount || 0);
  if (b?.balance_paid) return 0;
  const paid = (b?.deposit_paid ? dep : 0) + manual;
  return Math.max(0, +(total - paid).toFixed(2));
};

export const MyBookings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [payBooking, setPayBooking] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [redeemBooking, setRedeemBooking] = useState(null);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [waiverMap, setWaiverMap] = useState({});

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  // Live sync from the admin CRM: when an admin edits/cancels one of this
  // customer's bookings or updates their profile, the backend fires a
  // `booking_updated` / `customer_updated` SSE event targeted at this user
  // — refetch so the screen stays in sync without a manual reload.
  useMessageStream((evt) => {
    if (!evt) return;
    if (evt.type === 'booking_updated' || evt.type === 'customer_updated') {
      fetchData();
    }
  }, { enabled: !!user });

  const fetchData = async () => {
    try {
      const [bookRes, invRes] = await Promise.all([
        axios.get(`${API_URL}/api/bookings/user/my-bookings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/my-invoices`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setBookings(bookRes.data);
      setInvoices(invRes.data);
      try {
        const wRes = await axios.get(`${API_URL}/api/waiver/my-status`, { withCredentials: true });
        setWaiverMap(wRes.data?.statuses || {});
      } catch (_) { /* non-blocking */ }
    } catch (error) {
      console.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/my-invoices/${invoiceId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (e) { toast.error('Failed to download PDF'); }
  };

  const viewInvoice = (inv) => {
    setSelectedInvoice(inv);
    setInvoiceOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-[#06D6A0]/10 text-[#06D6A0]';
      case 'pending_hold': return 'bg-[#FF7A2E]/15 text-[#FF7A2E]';
      case 'pending_payment': return 'bg-yellow-100 text-yellow-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPaymentStatus = (booking) => {
    if (booking.payment_status === 'fully_paid') return { label: 'Fully Paid', color: 'text-[#06D6A0]' };
    if (booking.payment_status === 'deposit_paid') return { label: 'Deposit Paid', color: 'text-yellow-600' };
    return { label: 'Payment Pending', color: 'text-[#FF3B6B]' };
  };

  const getInvStatusBadge = (s) => {
    const m = { paid: 'bg-green-100 text-green-700', partial: 'bg-yellow-100 text-yellow-700', unpaid: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${m[s] || m.unpaid}`}>{s}</span>;
  };

  // iter-134 — split out expired Pay-Later holds that are still re-bookable
  // so they get their own section and the main "Bookings" list stays clean.
  const rebookable = (bookings || []).filter(
    (b) => b.status === 'cancelled' && b.pay_later_attempted && !b.rebooked_to_booking_id,
  );
  const activeBookings = (bookings || []).filter((b) => !rebookable.includes(b));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              My Bookings
            </h1>
            <p className="text-[#6B6B7A]">View and manage your party bookings</p>
          </div>
          <Button
            onClick={() => navigate('/book')}
            className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full btn-hover"
            data-testid="new-booking-btn"
          >
            Book New Party
          </Button>
        </div>

        {/* Tab Toggle */}
        <div className="flex gap-2 mb-6">
          <Button onClick={() => setActiveTab('bookings')}
            className={`rounded-full ${activeTab === 'bookings' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`}
            data-testid="tab-bookings">
            <Calendar className="w-4 h-4 mr-2" /> Bookings ({bookings.length})
          </Button>
          <Button onClick={() => setActiveTab('invoices')}
            className={`rounded-full ${activeTab === 'invoices' ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`}
            data-testid="tab-invoices">
            <FileText className="w-4 h-4 mr-2" /> Invoices ({invoices.length})
          </Button>
        </div>

        {/* iter-134 — Expired Pay-Later bookings the customer can re-book */}
        {activeTab === 'bookings' && rebookable.length > 0 && (
          <ExpiredBookingsSection bookings={rebookable} onRebooked={fetchData} />
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <>
            {activeBookings.length === 0 ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-[#E8E5DF] p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-[#FF3B6B]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>No Bookings Yet</h3>
                <p className="text-[#6B6B7A] mb-6">Ready to plan an amazing birthday party?</p>
                <Button onClick={() => navigate('/book')} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full btn-hover">
                  Book a Party <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {activeBookings.map((booking, index) => {
                  const paymentInfo = getPaymentStatus(booking);
                  return (
                    <motion.div key={booking.booking_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }} className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden card-hover"
                      data-testid={`booking-${booking.booking_id}`}>
                      <div className="p-6">
                        <div className="flex flex-col sm:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                              <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                                {booking.child_name}'s Party
                              </h3>
                              {booking.lifecycle_status ? (
                                <BookingStatusBadge status={booking.lifecycle_status} note={booking.status_note} size="lg" testid={`mb-status-${booking.booking_id}`} />
                              ) : (
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>{booking.status}</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#6B6B7A]" /><span className="text-[#1E1E24]">{new Date(booking.date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span></div>
                              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#6B6B7A]" /><span className="text-[#1E1E24]">{booking.time_slot === '10:00' ? '10 AM' : booking.time_slot === '13:00' ? '1 PM' : '4 PM'}</span></div>
                              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#6B6B7A]" /><span className="text-[#1E1E24]">{booking.guest_count} guests</span></div>
                              <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#6B6B7A]" /><span className={`font-medium ${paymentInfo.color}`}>{paymentInfo.label}</span></div>
                            </div>
                            {booking.status_note && (
                              <p className="text-xs text-[#6B6B7A] mt-3 flex items-start gap-1.5" data-testid={`mb-status-note-${booking.booking_id}`}>
                                <span className="w-1.5 h-1.5 mt-1.5 rounded-full flex-shrink-0" style={{ background: booking.status_color || '#9CA3AF' }} />
                                <span>{booking.status_note}</span>
                              </p>
                            )}
                          </div>
                          <div className="flex sm:flex-col items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm text-[#6B6B7A]">{booking.package_name}</p>
                              <p className="text-lg font-bold text-[#1E1E24]">${booking.total?.toFixed(2)}</p>
                            </div>
                            <div className="flex gap-2">
                              {booking.status !== 'cancelled' && (
                                <Button size="sm" variant="outline" onClick={() => { setEditBooking(booking); setEditOpen(true); }}
                                  className="rounded-full border-[#E8E5DF] text-[#6B6B7A]" data-testid={`edit-extras-${booking.booking_id}`}>
                                  <Pencil className="w-3 h-3 mr-1" /> Edit extras
                                </Button>
                              )}
                              {booking.status !== 'cancelled' && (
                                <Button size="sm" variant="outline" onClick={() => { setRescheduleBooking(booking); setRescheduleOpen(true); }}
                                  className="rounded-full border-[#E8E5DF] text-[#6B6B7A]" data-testid={`reschedule-${booking.booking_id}`}>
                                  <RefreshCw className="w-3 h-3 mr-1" /> Modify
                                </Button>
                              )}
                              {booking.status !== 'cancelled' && (
                                <Button size="sm" variant="outline" onClick={() => navigate(`/my-invites/new?from_booking=${booking.booking_id}`)}
                                  className="rounded-full border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/10" data-testid={`invite-guests-${booking.booking_id}`}>
                                  <Mail className="w-3 h-3 mr-1" /> Invite Guests
                                </Button>
                              )}
                              {booking.status !== 'cancelled' && booking.date >= new Date().toISOString().slice(0, 10) && (
                                <Button size="sm" variant="outline" onClick={() => { setRedeemBooking(booking); setRedeemOpen(true); }}
                                  className="rounded-full border-[#FB923C]/40 text-[#B8860B] hover:bg-[#FFF4E6]" data-testid={`mb-redeem-reward-${booking.booking_id}`}>
                                  <PartyPopper className="w-3 h-3 mr-1" /> Redeem reward
                                </Button>
                              )}
                              {booking.payment_status !== 'fully_paid' && booking.status !== 'cancelled' && (
                                <Button size="sm" onClick={() => { setPayBooking({ ...booking, outstanding: remainingOf(booking) }); setPayOpen(true); }}
                                  className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid={`pay-${booking.booking_id}`}>
                                  Pay Now
                                </Button>
                              )}
                              {booking.status !== 'cancelled' && (
                                waiverMap[booking.booking_id] ? (
                                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#06D6A0]/15 text-[#0F8B5C]"
                                    title={`${waiverMap[booking.booking_id].count || 1} guest waiver${(waiverMap[booking.booking_id].count || 1) === 1 ? '' : 's'} signed · latest valid until ${String(waiverMap[booking.booking_id].valid_until || '').slice(0, 10)}`}
                                    data-testid={`mb-waiver-ok-${booking.booking_id}`}>
                                    <CheckCircle2 className="w-3 h-3" />
                                    {waiverMap[booking.booking_id].count || 1} waiver{(waiverMap[booking.booking_id].count || 1) === 1 ? '' : 's'} signed
                                  </span>
                                ) : (
                                  <Button size="sm" variant="outline"
                                    onClick={() => navigate(`/waiver?source=portal&booking_id=${booking.booking_id}&child_name=${encodeURIComponent(booking.child_name || '')}`)}
                                    className="rounded-full border-[#FF3B6B]/40 text-[#FF3B6B] hover:bg-[#FF3B6B]/10" data-testid={`mb-sign-waiver-${booking.booking_id}`}>
                                    <ShieldCheck className="w-3 h-3 mr-1" /> Sign waiver
                                  </Button>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {!booking.allergy_form_completed && (
                        <div className="px-6 py-3 bg-yellow-50 border-t border-yellow-200 flex items-center justify-between">
                          <p className="text-sm text-yellow-700">Allergy form not completed</p>
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/booking/${booking.booking_id}/allergy-form`)} className="text-yellow-700 hover:text-yellow-800">
                            Complete Form <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      )}
                      <GuestAllergyForms
                        bookingId={booking.booking_id}
                        customerName={user?.name || ''}
                      />
                      {booking.status !== 'cancelled' && (
                        <details className="border-t border-[#E8E5DF] bg-[#FFFDF8]" data-testid={`mb-extras-${booking.booking_id}`}>
                          <summary className="px-6 py-3 cursor-pointer text-sm text-[#6B6B7A] hover:text-[#FF3B6B] flex items-center gap-2">
                            <Gift className="w-3.5 h-3.5" /> Apply a discount or gift card · view full breakdown
                          </summary>
                          <div className="px-6 py-4 grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-[#6B6B7A] font-semibold mb-2">Booking Totals</p>
                              <BookingTotalsSummary booking={booking} compact />
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-[#6B6B7A] font-semibold mb-2">Adjustments</p>
                              <BookingDiscountsWidget
                                booking={booking}
                                mode="customer"
                                compact
                                onUpdated={(updated) => {
                                  if (updated) {
                                    setBookings(prev => prev.map(b => b.booking_id === updated.booking_id ? { ...b, ...updated } : b));
                                  }
                                }}
                              />
                              <p className="text-[10px] text-[#6B6B7A] italic mt-2">To remove a code, contact support.</p>
                            </div>
                          </div>
                        </details>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <>
            {invoices.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E8E5DF] p-12 text-center">
                <FileText className="w-12 h-12 text-[#6B6B7A] mx-auto mb-4" />
                <p className="text-[#6B6B7A]">No invoices yet. Invoices appear after your booking is processed.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((inv, i) => (
                  <motion.div key={inv.invoice_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl border border-[#E8E5DF] p-6 card-hover"
                    data-testid={`invoice-${inv.invoice_id}`}>
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm font-bold text-[#FF3B6B]">{inv.invoice_id}</span>
                          {getInvStatusBadge(inv.status)}
                        </div>
                        <p className="text-sm text-[#1E1E24] font-medium">{inv.package_name} - {inv.child_name}'s Birthday</p>
                        <p className="text-xs text-[#6B6B7A]">Party Date: {inv.party_date} | Due: {inv.due_date}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#1E1E24]">${inv.total?.toFixed(2)}</p>
                          {inv.balance_due > 0 && (
                            <p className="text-xs text-[#FF3B6B]">Balance: ${inv.balance_due?.toFixed(2)}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => viewInvoice(inv)}
                            className="rounded-full border-[#E8E5DF]" data-testid={`view-inv-${inv.invoice_id}`}>
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => downloadPdf(inv.invoice_id)}
                            className="rounded-full border-[#E8E5DF] text-[#FF3B6B]" data-testid={`dl-inv-${inv.invoice_id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          {inv.balance_due > 0 && (
                            <Button size="sm" onClick={() => navigate(`/booking/${inv.booking_id}/payment`)}
                              className="rounded-full bg-[#FF3B6B] text-white" data-testid={`pay-inv-${inv.invoice_id}`}>
                              <CreditCard className="w-4 h-4 mr-1" /> Pay
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Invoice Detail Dialog */}
        <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Invoice</DialogTitle></DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-lg font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>Creative Birthdays</p>
                    <p className="text-xs text-[#6B6B7A]">Invoice #{selectedInvoice.invoice_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#6B6B7A]">Due: {selectedInvoice.due_date}</p>
                    {getInvStatusBadge(selectedInvoice.status)}
                  </div>
                </div>
                <div className="border-t border-[#E8E5DF] pt-4">
                  <div className="flex justify-between text-sm mb-1"><span>Package: {selectedInvoice.package_name}</span><span>${selectedInvoice.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm mb-1"><span>Tax (13%)</span><span>${selectedInvoice.tax?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm font-bold pt-2 border-t"><span>Total</span><span>${selectedInvoice.total?.toFixed(2)}</span></div>
                </div>
                <div className="bg-[#FFFDF8] rounded-xl p-4 space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-[#06D6A0]">Paid</span><span className="text-[#06D6A0] font-medium">${selectedInvoice.amount_paid?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#FF3B6B]">Balance</span><span className="text-[#FF3B6B] font-medium">${selectedInvoice.balance_due?.toFixed(2)}</span></div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => downloadPdf(selectedInvoice.invoice_id)} className="flex-1 bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="dialog-dl-pdf">
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  {selectedInvoice.balance_due > 0 && (
                    <Button onClick={() => { setInvoiceOpen(false); navigate(`/booking/${selectedInvoice.booking_id}/payment`); }}
                      className="flex-1 bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="dialog-pay-btn">
                      <CreditCard className="w-4 h-4 mr-2" /> Pay Balance
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reschedule Modal */}
        <RescheduleModal
          booking={rescheduleBooking}
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          onSuccess={fetchData}
        />

        {/* Customer self-edit extras dialog */}
        <CustomerEditBookingDialog
          booking={editBooking}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={fetchData}
        />

        {/* Unified on-page card payment modal */}
        <PaymentModal
          open={payOpen}
          onOpenChange={setPayOpen}
          booking={payBooking}
          mode="customer"
          paymentType="balance"
          title="Pay your balance"
          onSuccess={fetchData}
        />

        {/* Redeem loyalty points for a free add-on on this booking */}
        <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
          <DialogContent className="max-w-xl bg-white max-h-[88vh] overflow-y-auto" data-testid="mb-redeem-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Fredoka' }}>
                Redeem a free add-on{redeemBooking ? ` for ${redeemBooking.child_name}'s party` : ''}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#6B6B7A] -mt-2 mb-2">
              Use your loyalty points to add an extra to this party at no charge. Remove it anytime to get your points back.
            </p>
            {redeemBooking && (
              <AddonRedeemPanel
                variant="dialog"
                lockedBookingId={redeemBooking.booking_id}
                onChanged={fetchData}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};


// iter-134 — Section showing cancelled Pay-Later holds that the customer
// can re-book if the original slot is still available.
const ExpiredBookingsSection = ({ bookings, onRebooked }) => {
  const [availability, setAvailability] = useState({});
  const [rebookingId, setRebookingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check availability for each rebookable booking in parallel
    let cancelled = false;
    (async () => {
      const results = {};
      await Promise.all(
        bookings.map(async (b) => {
          try {
            const res = await axios.get(
              `${API_URL}/api/bookings/${b.booking_id}/rebook-availability`,
              { withCredentials: true },
            );
            results[b.booking_id] = res.data;
          } catch (e) {
            results[b.booking_id] = { available: false, reason: 'Check failed' };
          }
        }),
      );
      if (!cancelled) setAvailability(results);
    })();
    return () => { cancelled = true; };
  }, [bookings]);

  const handleRebook = async (bookingId) => {
    setRebookingId(bookingId);
    try {
      const res = await axios.post(
        `${API_URL}/api/bookings/${bookingId}/rebook`,
        {},
        { withCredentials: true },
      );
      toast.success('New booking created — please complete payment to confirm.');
      // Refresh parent list (so the new booking shows up) then redirect
      await onRebooked?.();
      navigate(res.data.redirect || `/booking/${res.data.booking_id}/payment`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-booking failed. Please try again.');
    } finally {
      setRebookingId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
      data-testid="expired-bookings-section"
    >
      <div className="bg-[#FFF4E6] border-2 border-[#FFD166] rounded-2xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#FFD166]/40 flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-[#B8860B]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Expired Bookings — You can re-book
            </h3>
            <p className="text-sm text-[#6B6B7A] mt-0.5">
              These Pay-Later bookings were auto-cancelled because the deposit wasn't received in time.
              You can re-book if the date is still available — this time you'll need to pay 50% deposit
              or the full amount upfront to confirm.
            </p>
          </div>
        </div>
        <div className="space-y-3">
          {bookings.map((b) => {
            const avail = availability[b.booking_id];
            const checking = !avail;
            const canRebook = avail?.available;
            return (
              <div
                key={b.booking_id}
                className="bg-white rounded-xl border border-[#E8E5DF] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`expired-booking-${b.booking_id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#1E1E24]">{b.child_name}'s Party</span>
                    <Badge className="bg-red-100 text-red-700 text-[10px]">Cancelled</Badge>
                    {canRebook && (
                      <Badge className="bg-[#06D6A0]/15 text-[#0F8B5C] text-[10px]" data-testid={`expired-avail-yes-${b.booking_id}`}>
                        Slot available
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-[#6B6B7A] mt-1 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(b.date).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {b.time_slot === '10:00' ? '10 AM' : b.time_slot === '13:00' ? '1 PM' : '4 PM'}
                    </span>
                    <span>{b.package_name}</span>
                    <span>{b.guest_count} guests</span>
                    <span className="font-medium text-[#1E1E24]">${(b.total || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex flex-col sm:items-end gap-1">
                  {checking ? (
                    <Button size="sm" disabled variant="outline" className="rounded-full text-xs"
                      data-testid={`expired-checking-${b.booking_id}`}>
                      Checking availability…
                    </Button>
                  ) : canRebook ? (
                    <Button
                      size="sm"
                      onClick={() => handleRebook(b.booking_id)}
                      disabled={rebookingId === b.booking_id}
                      className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
                      data-testid={`rebook-btn-${b.booking_id}`}
                    >
                      {rebookingId === b.booking_id ? (
                        <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Creating…</span>
                      ) : (
                        <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> Re-book now</span>
                      )}
                    </Button>
                  ) : (
                    <div className="text-right" data-testid={`expired-unavail-${b.booking_id}`}>
                      <p className="text-xs text-red-600 font-medium">Date no longer available</p>
                      <p className="text-[10px] text-[#6B6B7A]">{avail?.reason || 'Please contact us.'}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};


export default MyBookings;
